/**
 * The file drop zone's queue (piece 4): several files at once, progress per
 * file, and failure per file with a retry that leaves the others alone. The
 * zone itself is a native file input with drop events added; each file's
 * progress is a native progress element.
 */
/** 🔒 `stopped` is a file the window's Stop reached before it landed (decision 2422); it offers no retry. */
export type UploadStatus = 'queued' | 'uploading' | 'done' | 'failed' | 'stopped';

export interface QueuedFile {
  id: string;
  name: string;
  size: number;
  progress: number;
  status: UploadStatus;
  error: string | null;
}

export type UploadEvent =
  | { type: 'add'; files: readonly { id: string; name: string; size: number }[] }
  | { type: 'start'; id: string }
  | { type: 'progress'; id: string; progress: number }
  | { type: 'done'; id: string }
  | { type: 'fail'; id: string; error: string }
  | { type: 'retry'; id: string }
  | { type: 'remove'; id: string }
  | { type: 'stop' };

export function uploadQueueReducer(state: readonly QueuedFile[], event: UploadEvent): QueuedFile[] {
  switch (event.type) {
    case 'add':
      return [...state, ...event.files.map((f) => ({ ...f, progress: 0, status: 'queued' as const, error: null }))];
    case 'start':
      return state.map((f) => (f.id === event.id ? { ...f, status: 'uploading', progress: 0, error: null } : f));
    case 'progress':
      return state.map((f) => (f.id === event.id ? { ...f, progress: Math.max(0, Math.min(1, event.progress)) } : f));
    // 🔨 A request Stop aborted can still answer that it landed, and then it reads Done: what the server holds wins.
    case 'done':
      return state.map((f) => (f.id === event.id ? { ...f, status: 'done', progress: 1 } : f));
    // A stopped row stays stopped when its aborted request answers with the abort.
    case 'fail':
      return state.map((f) => (f.id === event.id && f.status !== 'stopped' ? { ...f, status: 'failed', error: event.error } : f));
    case 'retry':
      return state.map((f) => (f.id === event.id && f.status === 'failed' ? { ...f, status: 'queued', progress: 0, error: null } : f));
    case 'remove':
      return state.filter((f) => f.id !== event.id);
    // 🔒 Stop (decision 2422): every file not yet landed, queued or in flight, is stopped; a landed one stays Done.
    case 'stop':
      return state.map((f) => (f.status === 'queued' || f.status === 'uploading' ? { ...f, status: 'stopped' } : f));
  }
}

/** Whether any file still waits or travels, which is what holds the window's Done (decision 2422). */
export function queueBusy(state: readonly QueuedFile[]): boolean {
  return state.some((f) => f.status === 'queued' || f.status === 'uploading');
}

/** The names of the files that landed, in queue order, which Stop names (decision 2422). */
export function landedNames(state: readonly QueuedFile[]): string[] {
  return state.filter((f) => f.status === 'done').map((f) => f.name);
}

export function nextQueued(state: readonly QueuedFile[]): QueuedFile | undefined {
  return state.find((f) => f.status === 'queued');
}

/**
 * The upload the drop zone runs per queued file. `blob` is the bytes where the
 * caller has them — a page's real upload — and absent in the harness, whose
 * upload is a timer. 💰 The zone handed over a name and a size alone, so no
 * page could send a file anywhere (found while building, 2026-09-09).
 */
export type UploadFn = (file: { id: string; name: string; size: number; blob?: Blob; fileTypeId?: string | null }, onProgress: (fraction: number) => void, signal: AbortSignal) => Promise<void>;
